/**
 * SimilarPlayersFilterContentView - Filter sections for similar players
 *
 * Renders sort options, league multi-select, and range sliders for age, cost,
 * and contract months remaining. Extracted so the parent can choose
 * ScrollView (desktop sidebar) or plain View (mobile modal) as the wrapper,
 * avoiding nested-ScrollView gesture conflicts on mobile. SE-5570: the desktop
 * sort dropdown renders through the shared `SimilarPlayersSortDropdown`
 * molecule (on the SelectDropdown / FixedPositionDropdownShell) instead of the
 * retired gluestack Menu atom; the mobile branch keeps the inline
 * `SortOptionsList` unchanged.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { ArrowDownAZ, ArrowUpZA } from "lucide-react-native";
import React, { memo } from "react";

import {
  SortFieldCallbackMap,
  SimilarPlayersFilterData,
  SimilarPlayersFilterCallbacks,
} from "@/features/player-detail/types";
import type { OptionPickerProps } from "@/features/player-detail/utils/similarPlayersQueryState";
import SortOptionsList from "@/features/player-detail/components/molecules/SortOptionsList";
import SimilarPlayersSortDropdown from "@/features/player-detail/components/molecules/SimilarPlayersSortDropdown";

import {
  MAX_AGE,
  MAX_COST,
  MAX_MINUTES_PLAYED,
  MAX_MONTHS_REMAINING,
  MIN_AGE,
  MIN_COST,
  MIN_MINUTES_PLAYED,
  MIN_MONTHS_REMAINING,
} from "@/features/similar-players/stores/filterValues";

import CustomMultiSelectContainer from "@/components/organisms/MultiSelect";
import MultiSlider from "@/components/organisms/MultiSlider";
import { Box } from "@/components/atoms/Box";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";

import {
  RosterManagementLeagueFragment,
  RosterManagementNationalityFragment,
  SortOrder,
} from "@/generated/graphql";

/**
 * Props for SimilarPlayersFilterContentView component.
 * Filter data + change callbacks come from the shared interfaces; the content
 * view drives sort via pre-bound `sortFieldCallbacks`, so `onSortFieldChange`
 * is omitted.
 */
interface SimilarPlayersFilterContentViewProps
  extends
    Omit<SimilarPlayersFilterData, "leaguesState" | "nationalitiesState">,
    Omit<SimilarPlayersFilterCallbacks, "onSortFieldChange"> {
  /** Leagues picker props, already flattened from the query state (SE-7580) */
  readonly leaguesPicker: OptionPickerProps<RosterManagementLeagueFragment>;
  /** Nationalities picker props, already flattened from the query state */
  readonly nationalitiesPicker: OptionPickerProps<RosterManagementNationalityFragment>;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Pre-bound callbacks for each sort field to avoid inline function creation */
  readonly sortFieldCallbacks: SortFieldCallbackMap;
  /** Whether to render mobile-optimized sort options instead of dropdown menu */
  readonly isFullWidth?: boolean;
}

/**
 * Renders the filter sections (sort, leagues, sliders) for similar players
 * @param props - Component props
 * @param props.isDark - Whether dark mode is active
 * @param props.sortOptions - Available sort options
 * @param props.selectedSortField - Currently selected sort field
 * @param props.selectedSortLabel - Display label for selected sort option
 * @param props.sortOrder - Current sort order
 * @param props.leaguesPicker - Flattened leagues picker props
 * @param props.selectedLeagues - Currently selected leagues
 * @param props.nationalitiesPicker - Flattened nationalities picker props
 * @param props.selectedNationalities - Currently selected nationalities
 * @param props.ageRange - Current age range values [min, max]
 * @param props.costRange - Current cost range values [min, max]
 * @param props.monthsRemainingRange - Current months remaining range values
 * @param props.minutesPlayedRange - Current minutes played range values
 * @param props.onSortOrderToggle - Callback to toggle sort order
 * @param props.onLeaguesChange - Callback when leagues selection changes
 * @param props.onNationalitiesChange - Callback when nationalities selection changes
 * @param props.onAgeRangeChange - Callback when age range changes
 * @param props.onCostRangeChange - Callback when cost range changes
 * @param props.onMonthsRemainingRangeChange - Callback when months range changes
 * @param props.onMinutesPlayedRangeChange - Callback when minutes played range changes
 * @param props.onRetryLeagues - Retries the leagues query after a failure
 * @param props.onRetryNationalities - Retries the nationalities query after a failure
 * @param props.sortFieldCallbacks - Pre-bound callbacks for each sort field
 * @param props.isFullWidth - Whether to render mobile-optimized sort options
 * @returns Fragment containing all filter sections
 */
const SimilarPlayersFilterContentView = ({
  isDark,
  sortOptions,
  selectedSortField,
  selectedSortLabel,
  sortOrder,
  leaguesPicker,
  selectedLeagues,
  nationalitiesPicker,
  selectedNationalities,
  ageRange,
  costRange,
  monthsRemainingRange,
  minutesPlayedRange,
  onSortOrderToggle,
  onLeaguesChange,
  onNationalitiesChange,
  onAgeRangeChange,
  onCostRangeChange,
  onMonthsRemainingRangeChange,
  onMinutesPlayedRangeChange,
  onRetryLeagues,
  onRetryNationalities,
  sortFieldCallbacks,
  isFullWidth,
}: SimilarPlayersFilterContentViewProps) => (
  <Stack className="gap-6">
    {/* Sort Section */}
    <Stack className="gap-2">
      <Heading
        className="text-caption uppercase text-content-muted"
        variant="title-sm"
      >
        Sort
      </Heading>
      {isFullWidth ? (
        <SortOptionsList
          isDark={isDark}
          sortOptions={sortOptions}
          selectedSortField={selectedSortField}
          selectedSortLabel={selectedSortLabel}
          sortOrder={sortOrder}
          sortFieldCallbacks={sortFieldCallbacks}
          onSortOrderToggle={onSortOrderToggle}
        />
      ) : (
        <Stack direction="horizontal" className="gap-2">
          <Box className="flex-1">
            <SimilarPlayersSortDropdown
              isDark={isDark}
              sortOptions={sortOptions}
              selectedSortField={selectedSortField}
              selectedSortLabel={selectedSortLabel}
              sortFieldCallbacks={sortFieldCallbacks}
            />
          </Box>
          <Pressable
            onPress={onSortOrderToggle}
            className="size-10 items-center justify-center rounded-none border border-outline-subtle bg-surface-raised active:bg-surface-subtle"
          >
            <Icon
              as={sortOrder === SortOrder.Desc ? ArrowDownAZ : ArrowUpZA}
              size="sm"
              className={isDark ? "text-white" : "text-content-primary"}
            />
          </Pressable>
        </Stack>
      )}
    </Stack>

    {/* Leagues Section. SE-7580: a failed leagues read now renders as an
        error + retry inside the picker instead of "No Leagues", which read as
        an authoritative "your organization has no leagues". */}
    <Box>
      <CustomMultiSelectContainer<RosterManagementLeagueFragment>
        data={leaguesPicker.data as RosterManagementLeagueFragment[]}
        listSelected={selectedLeagues as RosterManagementLeagueFragment[]}
        title="Leagues"
        loading={leaguesPicker.loading}
        errorMessage={leaguesPicker.errorMessage}
        noticeMessage={leaguesPicker.noticeMessage}
        onRetry={onRetryLeagues}
        enableSearch
        searchPlaceholder="Search leagues"
        onSelect={
          onLeaguesChange as (leagues: RosterManagementLeagueFragment[]) => void
        }
      />
    </Box>

    {/* Nationalities Section */}
    <Box>
      <CustomMultiSelectContainer<RosterManagementNationalityFragment>
        data={nationalitiesPicker.data as RosterManagementNationalityFragment[]}
        listSelected={
          selectedNationalities as RosterManagementNationalityFragment[]
        }
        title="Nationalities"
        loading={nationalitiesPicker.loading}
        errorMessage={nationalitiesPicker.errorMessage}
        noticeMessage={nationalitiesPicker.noticeMessage}
        onRetry={onRetryNationalities}
        enableSearch
        searchPlaceholder="Search nationalities"
        onSelect={
          onNationalitiesChange as (
            nationalities: RosterManagementNationalityFragment[]
          ) => void
        }
      />
    </Box>

    {/* Age Slider */}
    <Stack className="gap-3">
      <Heading
        className="text-caption uppercase text-content-muted"
        variant="title-sm"
      >
        Age
      </Heading>
      <MultiSlider
        value={[ageRange[0], ageRange[1]]}
        minValue={MIN_AGE}
        maxValue={MAX_AGE}
        onChange={onAgeRangeChange}
      />
    </Stack>

    {/* Cost Slider */}
    <Stack className="gap-3">
      <Heading
        className="text-caption uppercase text-content-muted"
        variant="title-sm"
      >
        Cost
      </Heading>
      <MultiSlider
        value={[costRange[0], costRange[1]]}
        step={100000}
        minValue={MIN_COST}
        maxValue={MAX_COST}
        isCurrency
        useLogScale
        onChange={onCostRangeChange}
      />
    </Stack>

    {/* Months Remaining Slider */}
    <Stack className="gap-3">
      <Heading
        className="text-caption uppercase text-content-muted"
        variant="title-sm"
      >
        Months Remaining
      </Heading>
      <MultiSlider
        value={[monthsRemainingRange[0], monthsRemainingRange[1]]}
        step={1}
        minValue={MIN_MONTHS_REMAINING}
        maxValue={MAX_MONTHS_REMAINING}
        onChange={onMonthsRemainingRangeChange}
      />
    </Stack>

    {/* Minutes Played Slider */}
    <Stack className="gap-3">
      <Heading
        className="text-caption uppercase text-content-muted"
        variant="title-sm"
      >
        Minutes Played
      </Heading>
      <MultiSlider
        value={[minutesPlayedRange[0], minutesPlayedRange[1]]}
        step={100}
        minValue={MIN_MINUTES_PLAYED}
        maxValue={MAX_MINUTES_PLAYED}
        onChange={onMinutesPlayedRangeChange}
      />
    </Stack>
  </Stack>
);

SimilarPlayersFilterContentView.displayName = "SimilarPlayersFilterContentView";

export default memo(SimilarPlayersFilterContentView);
